// src/components/JobsTable.tsx - FIXED: Show correct unlock/add counts in job history
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  RefreshCw, 
  Download, 
  X, 
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAppContext } from '../hooks/useAppContext';
import { API_BASE_URL } from '../constants';
import type { Job, JobResult } from '../types';

interface JobsTableProps {
  isAdminMode?: boolean;
  showUserColumn?: boolean;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  maxHeight?: string;
  onUserClick?: (user: any) => void;
}

interface JobsResponse {
  jobs: Job[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

const JobsTable: React.FC<JobsTableProps> = ({
  isAdminMode = false,
  showUserColumn = false,
  emptyStateTitle = "No Jobs Found",
  emptyStateDescription = "Background jobs will appear here once you start running operations",
  maxHeight = "600px"
}) => {
  const { apiKey } = useAuth();
  const { state } = useAppContext();
  const { activeJobs } = state;
  
  const [jobHistory, setJobHistory] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());


  // Merge job history with active jobs for live progress updates
  const mergedJobs = useMemo(() => {
    const activeJobsMap = new Map(activeJobs.map(job => [job.job_id, job]));
    const historyJobsMap = new Map(jobHistory.map(job => [job.job_id, job]));
    
    // Create a merged map where active jobs override history jobs
    const mergedMap = new Map([...historyJobsMap, ...activeJobsMap]);
    
    // Convert back to array and sort by creation date
    return Array.from(mergedMap.values()).sort((a, b) => {
      try {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } catch {
        return 0;
      }
    });
  }, [jobHistory, activeJobs]);

  // Fetch jobs with enhanced error handling
  const fetchJobHistory = useCallback(async () => {
    if (!apiKey) return;
    
    setLoading(true);
    
    try {
      let endpoint: string;
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', '50');
      
      if (isAdminMode) {
        endpoint = `${API_BASE_URL}/api/admin/jobs`;
        if (userFilter.trim()) params.append('owner_id', userFilter.trim());
        if (statusFilter !== 'all') {
          if (statusFilter === 'active') {
            params.append('status', 'pending,running');
          } else {
            params.append('status', statusFilter);
          }
        }
        if (jobFilter !== 'all') params.append('job_type', jobFilter);
      } else {
        endpoint = `${API_BASE_URL}/api/jobs`;
      }
      
      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data: JobsResponse = await response.json();
        const jobs = data.jobs || [];
        
        const sortedJobs = jobs
          .filter((job: Job) => job && job.job_id)
          .sort((a: Job, b: Job) => {
            try {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            } catch {
              return 0;
            }
          });
        
        setJobHistory(sortedJobs);
        setTotalPages(data.total_pages || 1);
        setLastRefresh(new Date());
      } else if (response.status === 401) {

        localStorage.setItem('show_login_page', 'true');
        window.location.reload();
        return;
      } else if (response.status === 404 && isAdminMode) {
        console.warn('Admin jobs endpoint not available yet');
        setJobHistory([]);
        setTotalPages(1);
        setLastRefresh(new Date());
      } else {
        setJobHistory([]);
        setTotalPages(1);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.warn('Job history endpoint error:', error);
      setJobHistory([]);
      setTotalPages(1);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, [apiKey, isAdminMode, page, userFilter, statusFilter, jobFilter]);

  // Load jobs on mount and when dependencies change
  useEffect(() => {
    fetchJobHistory();
  }, [fetchJobHistory]);

  // Auto-refresh job history when active jobs complete
  useEffect(() => {
    const hasCompletedJobs = activeJobs.some(job => 
      job.status === 'completed' || job.status === 'failed' || job.status === 'canceled'
    );
    
    if (hasCompletedJobs) {
      // Small delay to ensure the API has the latest data
      const timer = setTimeout(() => {
        fetchJobHistory();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [activeJobs, fetchJobHistory]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchJobHistory();
  }, [fetchJobHistory]);

  // Cancel job
  const cancelJob = useCallback(async (jobId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      
      if (response.ok) {
        fetchJobHistory();
      } else if (response.status === 401) {

        localStorage.setItem('show_login_page', 'true');
        window.location.reload();
        return;
      }
    } catch (error) {
      console.error(`Error cancelling job ${jobId}:`, error);
    }
  }, [apiKey, fetchJobHistory]);



  // Enhanced filtering
  const filteredJobs = useMemo(() => {
    try {
      return mergedJobs.filter(job => {
        if (!job || !job.job_id) return false;
        
        // Job type filter
        if (jobFilter !== 'all' && job.job_type !== jobFilter) return false;
        
        // Status filter
        if (statusFilter !== 'all') {
          if (statusFilter === 'active' && !(job.status === 'pending' || job.status === 'running')) return false;
          if (statusFilter !== 'active' && job.status !== statusFilter) return false;
        }
        
        return true;
      });
    } catch (err) {
      console.warn('Error filtering jobs:', err);
      return [];
    }
  }, [mergedJobs, jobFilter, statusFilter]);

  // FIXED: Enhanced job metadata extraction with correct unlock counting
  const getJobMetadata = useCallback((job: Job) => {
    const result = job.result as JobResult || {};
    const params = job.params || {};
    
    
    // FIXED: Use correct count based on job type and actual backend field names
    let displayCount = 0;
    if (job.job_type === 'contacts_unlock') {
      const netNew = result.net_new_contacts || 0;
      const refresh = result.refresh_contacts || 0;
      displayCount = netNew + refresh;
    } else if (job.job_type === 'accounts_unlock') {
      // For account unlock jobs, count the number of accounts added
      const added = result.accounts_added || 0;
      const updated = result.accounts_updated || 0;
      displayCount = added + updated;
    } else {
      displayCount = result.record_count || result.processed_count || 0;
    }
    
    // Set cost based on job type
    let totalCost = result.total_cost;
    if (job.job_type === 'accounts_unlock') {
      // Account unlock jobs are FREE - use the actual cost from result
      totalCost = result.total_cost || "0.00";
    }

    return {
      recordCount: displayCount,
      processedCount: result.processed_count || 0,
      requestedCount: result.requested_count || 0,
      deletedCount: result.deleted_count || 0,
      addedCount: result.added_count || 0,
      failedCount: result.failed_count || 0,
      fileName: result.file_name || '',
      fileSize: result.file_size_bytes || 0,
      downloadUrl: result.signed_download_url || result.download_url || '',
      totalCost: totalCost,
      updates: params.updates || {},
      filters: params.filters || {},
      selectAll: params.select_all || false,
      duration: (() => {
        if (job.completed_at && job.started_at) {
          const durationMs = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
          const duration = Math.max(1, Math.round(durationMs / 1000)); // Minimum 1 second for very fast jobs
          return duration;
        }
        return null;
      })(),
      throughputPerSecond: job.completed_at && job.started_at && displayCount ? 
        Math.round(displayCount / ((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)) : null
    };
  }, []);



  // Enhanced time formatting
  const formatRelativeTime = (dateString: string): string => {
    try {
      const now = new Date();
      const created = new Date(dateString);
      const diffMs = now.getTime() - created.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return created.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get user display name
  const getUserDisplayName = (job: Job): string => {
    if (job.owner_id && job.owner_id.length > 12) {
      return `${job.owner_id.slice(0, 8)}...`;
    }
    return job.owner_id || 'Unknown';
  };

  // Enhanced job title
  const getJobTitle = (job: Job): string => {
    const titles = {
      export: 'Data Export',
      add_to_list: 'Add to List',
      contacts_unlock: 'Unlock Contacts',
      accounts_unlock: 'Unlock Accounts',
      delete_contacts: 'Delete Contacts',
      remove_from_list: 'Remove from List',
      enrichment_export: 'Enrichment Export'
    };
    return titles[job.job_type as keyof typeof titles] || job.job_type;
  };

  // Job status statistics
  const jobStats = useMemo(() => {
    const stats = {
      all: mergedJobs.length,
      active: 0,
      completed: 0,
      failed: 0,
      canceled: 0,
      byType: {} as Record<string, number>
    };

    mergedJobs.forEach(job => {
      if (job.status === 'pending' || job.status === 'running') stats.active++;
      else if (job.status === 'completed') stats.completed++;
      else if (job.status === 'failed') stats.failed++;
      else if (job.status === 'canceled') stats.canceled++;
      
      stats.byType[job.job_type] = (stats.byType[job.job_type] || 0) + 1;
    });

    return stats;
  }, [mergedJobs]);

  if (loading && page === 1) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="loading-section">
            <RefreshCw size={32} className="loading-spinner" />
            <p>Loading job data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">Job History</h3>
            </div>
            
            {/* Enhanced Filter Controls */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <select 
                  value={jobFilter} 
                  onChange={(e) => setJobFilter(e.target.value)}
                  className="form-input"
                  style={{ minWidth: '160px' }}
                >
                  <option value="all">All Types ({jobStats.all})</option>
                  <optgroup label="By Type">
                    {Object.entries(jobStats.byType).map(([type, count]) => (
                      <option key={type} value={type}>
                        {getJobTitle({ job_type: type } as Job)} ({count})
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
                style={{ minWidth: '140px' }}
              >
                <option value="all">All Status ({jobStats.all})</option>
                <option value="active">Active ({jobStats.active})</option>
                <option value="completed">Completed ({jobStats.completed})</option>
                <option value="failed">Failed ({jobStats.failed})</option>
                <option value="canceled">Canceled ({jobStats.canceled})</option>
              </select>
              
              {showUserColumn && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Filter by user..."
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="form-input"
                    style={{ minWidth: '180px' }}
                  />
                </div>
              )}
            </div>
          </div>
          
          <button onClick={handleRefresh} className="btn btn-secondary btn-sm" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="card-body" style={{ maxHeight, overflowY: 'auto', padding: 0 }}>
        {filteredJobs.length > 0 ? (
          <div className="table-wrapper">
            <table className="table-premium">
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Operation</th>
                  {showUserColumn && <th style={{ width: '120px' }}>User</th>}
                  <th style={{ width: '120px' }}>Status</th>
                  <th style={{ width: '120px' }}>Progress</th>
                  <th style={{ width: '100px' }}>Records</th>
                  <th style={{ width: '120px' }}>Performance</th>
                  <th style={{ width: '100px' }}>Cost</th>
                  <th style={{ width: '120px' }}>Created</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job: Job) => {
                  if (!job || !job.job_id) return null;
                  
                  const metadata = getJobMetadata(job);
                  const isActive = job.status === 'pending' || job.status === 'running';
                  const canDownload = job.status === 'completed' && job.job_type === 'export' && metadata.downloadUrl;
                  const jobCost = metadata.totalCost;
                  const hasBilling = jobCost && parseFloat(String(jobCost)) > 0;
                  const showCost = hasBilling || (job.job_type === 'accounts_unlock' && jobCost !== null && jobCost !== undefined);
                  
                  return (
                    <tr key={job.job_id} className={`${isActive ? 'active' : ''} hover:bg-gray-25 transition-colors`}>
                      
                      <td>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {getJobTitle(job)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {job.job_id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {showUserColumn && (
                        <td>
                          <div className="text-center">
                            <span className="text-xs font-mono text-gray-600">
                              {getUserDisplayName(job)}
                            </span>
                          </div>
                        </td>
                      )}
                      
                      <td className="status-column">
                        <span className={`status-badge status-${job.status} ${isActive ? 'running has-pulse' : ''}`}>
                          {isActive && <div className="status-pulse" />}
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>
                      </td>
                      
                      <td>
                        {(job.progress > 0 || isActive) ? (
                          <div className="progress-container">
                            <div className="progress-bar">
                              <div 
                                className={`progress-fill ${job.status === 'failed' ? 'failed' : ''}`}
                                style={{ width: `${Math.min(100, job.progress)}%` }}
                              />
                            </div>
                            <div className="progress-meta text-xs mt-1">
                              {Math.round(Math.min(100, job.progress))}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      
                      <td>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">
                            {metadata.recordCount > 0 ? metadata.recordCount.toLocaleString() : '—'}
                          </div>
                          {metadata.processedCount > 0 && metadata.processedCount !== metadata.recordCount && (
                            <div className="text-xs text-gray-500">
                              {metadata.processedCount.toLocaleString()} processed
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="text-right">
                          {metadata.duration ? (
                            <div className="text-right">
                              <span className="text-sm font-medium">
                                {metadata.duration < 60 ? `${metadata.duration}s` : 
                                 metadata.duration < 3600 ? `${Math.round(metadata.duration / 60)}m` :
                                 `${Math.round(metadata.duration / 3600)}h`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                          {metadata.fileSize > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {formatFileSize(metadata.fileSize)}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="text-right">
                          {showCost ? (
                            <div className="text-right">
                              <span className="text-sm font-medium text-gray-900">
                                ${(() => {
                                  if (typeof jobCost === 'string') {
                                    return parseFloat(jobCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                  } else if (typeof jobCost === 'number') {
                                    return jobCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                  }
                                  return '0.00';
                                })()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      
                      <td>
                        <div className="flex flex-col gap-1">
                          <div>
                            <span className="font-medium text-sm">
                              {formatRelativeTime(job.created_at)}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(job.created_at).toLocaleString()}
                          </span>
                        </div>
                      </td>
                      
                      <td>
                        <div className="flex gap-2">
                          {canDownload && (
                            <button
                              className="btn-icon success btn-sm"
                              onClick={() => window.open(metadata.downloadUrl, '_blank')}
                              title={`Download ${metadata.fileName || 'export file'}`}
                            >
                              <Download size={14} />
                            </button>
                          )}
                          {isActive && (
                            <button
                              className="btn-icon danger btn-sm"
                              onClick={() => cancelJob(job.job_id)}
                              title="Cancel job"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-section">
            <h4>{emptyStateTitle}</h4>
            <p>{emptyStateDescription}</p>
            {isAdminMode && (
              <div className="warning-box mt-4">
                <strong>Note:</strong> Admin job monitoring requires API endpoint: <code>GET /api/admin/jobs</code>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Footer */}
      <div className="card-footer">
        <div className="flex items-center justify-between text-sm w-full">
          <div className="flex items-center gap-4 text-gray-600">
            <div>
              <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
            </div>
            <div>
              <span>{filteredJobs.length} of {mergedJobs.length} jobs</span>
            </div>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button 
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsTable;